import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../constants";
import { addMonths, dayOf, monthGrid, monthOf } from "../dayTime";

const useStyles = makeStyles({
  root: {
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    padding: 10,
    background: T.elevated,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  month: { fontSize: 13, fontWeight: 600, color: T.fg },
  nav: {
    border: `1px solid ${T.border}`,
    background: T.elevated,
    borderRadius: 6,
    padding: "2px 8px",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    color: T.fg,
    "&:disabled": { color: T.subtleFg, cursor: "default" },
  },
  grid: { display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4 },
  dow: {
    fontSize: 11,
    color: T.subtleFg,
    textAlign: "center",
    padding: "2px 0",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  day: {
    border: `1px solid ${T.borderSoft}`,
    borderRadius: 6,
    padding: "6px 0",
    fontSize: 12,
    background: T.elevated,
    cursor: "pointer",
    fontFamily: "inherit",
    color: T.fg,
    textAlign: "center",
    "&:disabled": { color: T.subtleFg, cursor: "default", background: T.surface },
    "&[aria-selected='true']": {
      background: T.accent,
      color: T.accentFg,
      borderColor: T.primary,
      fontWeight: 600,
    },
    "&[aria-current='date']": { fontWeight: 600 },
  },
  blank: { minHeight: 28 },
});

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function MonthCalendar({
  tz,
  now,
  selectedDay = null,
  onSelect,
  label = "Choose a date",
  allowPast = false,
}) {
  const s = useStyles();
  const today = dayOf(now, tz);
  const [month, setMonth] = useState(() => monthOf(selectedDay ?? today));
  const grid = monthGrid({ month, tz, now });

  return (
    <div className={s.root}>
      <div className={s.head}>
        <button
          type="button"
          className={s.nav}
          onClick={() => setMonth(addMonths(month, -1))}
          disabled={!allowPast && month <= monthOf(today)}
          aria-label="Previous month"
        >
          &lsaquo;
        </button>
        <span className={s.month}>{grid.label}</span>
        <button
          type="button"
          className={s.nav}
          onClick={() => setMonth(addMonths(month, 1))}
          aria-label="Next month"
        >
          &rsaquo;
        </button>
      </div>

      <div className={s.grid} role="grid" aria-label={label}>
        {DOW.map((item) => (
          <span key={item} className={s.dow} role="columnheader">
            {item}
          </span>
        ))}
        {Array.from({ length: grid.leading }, (_, index) => (
          <span key={`blank-${index}`} className={s.blank} aria-hidden="true" />
        ))}
        {grid.days.map((entry) => (
          <button
            key={entry.day}
            type="button"
            role="gridcell"
            className={s.day}
            disabled={entry.past && !allowPast}
            aria-selected={entry.day === selectedDay}
            aria-current={entry.today ? "date" : undefined}
            aria-label={entry.day}
            onClick={() => onSelect(entry.day)}
          >
            {entry.number}
          </button>
        ))}
      </div>
    </div>
  );
}
